
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MembershipWithServices, useUpdateMembership, useDeleteMembership } from "@/hooks/useMemberships";
import { Edit, Trash2, Package, RefreshCw } from "lucide-react";
import AddMembershipDialog from "./AddMembershipDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface MembershipCardProps {
  membership: MembershipWithServices;
  services: Array<{ id: string; name: string; service_type: string; }>;
  onEdit: (membership: MembershipWithServices) => void;
  onDelete: (membershipId: string) => void;
}

const MembershipCard = ({ membership, services, onEdit, onDelete }: MembershipCardProps) => {
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const getMembershipTypeDisplay = (type: string) => {
    switch (type) {
      case 'pack_of_sessions':
        return 'Pack of Sessions';
      case 'sessions_per_month':
        return 'Sessions per Month';
      case 'unlimited':
        return 'Unlimited';
      default:
        return type;
    }
  };

  const getMembershipTypeColor = (type: string) => {
    switch (type) {
      case 'pack_of_sessions':
        return 'bg-blue-100 text-blue-800';
      case 'sessions_per_month':
        return 'bg-green-100 text-green-800';
      case 'unlimited':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleEditClick = () => {
    setEditDialogOpen(true);
  };

  const handleEditComplete = () => {
    setEditDialogOpen(false);
  };

  const handleDeleteConfirm = () => {
    console.log('Delete confirmed for membership:', membership.id);
    onDelete(membership.id);
  };

  const getPriceDisplay = () => {
    if (membership.is_subscription) {
      return (
        <span className="text-2xl font-bold text-gray-900">
          {membership.currency} {membership.price.toFixed(2)}
          <span className="text-sm font-normal text-gray-500">/month</span>
        </span>
      );
    }
    return (
      <span className="text-2xl font-bold text-gray-900">
        {membership.currency} {membership.price.toFixed(2)}
      </span>
    );
  };

  const getSessionsDisplay = () => {
    if (membership.membership_type === 'unlimited') {
      return 'Unlimited sessions';
    }
    if (membership.membership_type === 'sessions_per_month' && membership.session_count) {
      return `${membership.session_count} sessions per month`;
    }
    if (membership.session_count) {
      return `${membership.session_count} sessions`;
    }
    return null;
  };

  return (
    <div className="relative">
      <Card className="h-full">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <CardTitle className="text-lg font-semibold">{membership.name}</CardTitle>
              <div className="flex gap-2 mt-2">
                <Badge className={`${getMembershipTypeColor(membership.membership_type)}`}>
                  {getMembershipTypeDisplay(membership.membership_type)}
                </Badge>
                {membership.is_subscription && (
                  <Badge variant="outline" className="border-green-500 text-green-700">
                    <RefreshCw className="h-3 w-3 mr-1" />
                    Subscription
                  </Badge>
                )}
              </div>
            </div>
            <div className="flex gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleEditClick}
                className="h-8 w-8 p-0"
              >
                <Edit className="h-4 w-4" />
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete Membership</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to delete "{membership.name}"? This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction 
                      onClick={handleDeleteConfirm}
                      className="bg-red-600 hover:bg-red-700"
                    >
                      Delete Membership
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {membership.description && (
            <p className="text-sm text-gray-600">{membership.description}</p>
          )}
          
          <div className="flex items-center justify-between">
            <div>
              {getPriceDisplay()}
              {getSessionsDisplay() && (
                <p className="text-sm text-gray-500">
                  {getSessionsDisplay()}
                </p>
              )}
            </div>
          </div>

          {membership.services.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Package className="h-4 w-4 text-gray-600" />
                <span className="text-sm font-medium text-gray-900">Included Services</span>
              </div>
              <div className="flex flex-wrap gap-1">
                {membership.services.map((service) => (
                  <Badge key={service.id} variant="secondary" className="text-xs">
                    {service.name}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {editDialogOpen && (
        <AddMembershipDialog
          providerId={membership.provider_id}
          services={services}
          membership={membership}
          isEdit={true}
          open={editDialogOpen}
          onOpenChange={setEditDialogOpen}
          onMembershipAdded={handleEditComplete}
        />
      )}
    </div>
  );
};

export default MembershipCard;
